/**
 * Block 14 «Раскрой» — PR #1 EP-1 (CUT-1.1). Zod-схемы модели данных.
 *
 * Источник истины — TRD §4.1 «Базовые типы» (вербатим, не выдумано). Размещены
 * **feature-local** (решение супервайзера PR #1 §1.1): в PR #1 схемы валидируют
 * только локальный in-memory стор. Серверные API-контракты (`cutting-*` в
 * `packages/shared`) появятся в PR #2 пакетным под-ADR на эпик (ADR-0062).
 *
 * MUST NOT нормализовать Global_ID/material (P-001) — здесь material = free-text
 * (каталог декоров НЕ в v1 Раскроя, §IX.X инвариант владельца).
 *
 * @see Помощник — Прототип Раскроя/03-tz-developer/03-TRD.md §4.1
 */

import { z } from 'zod';

// --- Базовые перечисления (TRD §4.1) ---

export const RotationModeSchema = z.enum(['allow', 'forbid', 'force']);
export const EdgeTypeSchema = z.enum(['type1', 'type2', 'type3', 'type4', '']);

export const EdgeAssignmentSchema = z.object({
  top: EdgeTypeSchema,
  bottom: EdgeTypeSchema,
  left: EdgeTypeSchema,
  right: EdgeTypeSchema,
});

// --- Пазы детали (PR #10b-1-GROOVE, CUT-3.3) ---
//
// Графический селектор пазов (project.html:761-790, app.js:208-280): на каждой
// стороне детали циклически Нет → V-паз → U-паз → L-четверть → Нет. Тип паза —
// одно из `v`/`u`/`l` или `''` (нет). Полностью аналог `EdgeAssignment`.
//
// ⚠️ Feature-local поле (как `edge.thickness`/`ProjectOffcut.name`, B-211/B-225):
// замороженный контракт `ProjectData.parts` (ADR-0065, P-011) НЕ содержит grooves.
// Персистится ТОЛЬКО через IDB-local (serialization.ts). В `documentToProjectData`
// grooves вырезаются (R2-честность). Server-side персистенция — будущий CUT + ADR.

export const GrooveTypeSchema = z.enum(['v', 'u', 'l', '']);

export const GrooveAssignmentSchema = z.object({
  top: GrooveTypeSchema,
  right: GrooveTypeSchema,
  bottom: GrooveTypeSchema,
  left: GrooveTypeSchema,
});

/** Пустое назначение пазов (все стороны «Нет»). */
export const EMPTY_GROOVES = { top: '', right: '', bottom: '', left: '' } as const;

// --- Деталь (TRD §4.1) ---

export const PartSchema = z.object({
  id: z.string(),
  length: z.number().positive().max(10000), // мм
  width: z.number().positive().max(10000),
  qty: z.number().int().positive().max(1000),
  rotation: RotationModeSchema,
  name: z.string().max(200),
  // `do` — legacy-флаг «есть обработка» для EP-4-движка (контракт ADR-0065). С
  // PR #10b-2 НЕ независимое поле: источник истины обработки — `processing` (ниже),
  // а `do` ПРОИЗВОДИТСЯ при записи в контракт (`do = processing !== ''`, см.
  // projectSync.toContractPart). В редакторе `do` «спящий» (chip читает processing).
  do: z.boolean(),
  doType: z.string().optional(), // 'milling' | 'drilling' | 'rounding' | 'engraving' | 'other'
  edges: EdgeAssignmentSchema,
  // Пазы детали (PR #10b-1-GROOVE) — feature-local, default «без пазов» (см.
  // GrooveAssignmentSchema). Не путать с `edges` (кромки): пазы — V/U/L-профили.
  grooves: GrooveAssignmentSchema.default({ top: '', right: '', bottom: '', left: '' }),
  // Обработка детали (PR #10b-2, CUT-3.3) — single free-text из словаря
  // `ProcessingDict` («Склейка»/«Фрезеровка»/свой вариант) или '' (без обработки).
  // ⚠️ Feature-local (как `grooves`): персистится только IDB-local (serialization),
  // в замороженный контракт `ProjectData.parts` НЕ идёт — `toContractPart` срезает
  // processing и выводит из него legacy `do` (R2-честность). Источник истины обработки.
  processing: z.string().max(200).default(''),
  material: z.string().optional(), // для multi-material проектов (free-text, P-001)
});

// --- Лист (TRD §4.1) ---

export const SheetSchema = z.object({
  id: z.string(),
  length: z.number().positive(),
  width: z.number().positive(),
  qty: z.number().int().positive(),
  isOffcut: z.boolean(),
  name: z.string().optional(),
  receivedAt: z.string().datetime().optional(),
  use: z.boolean(),
});

// --- Размер листа: uniform / mixed (TRD §4.1, §IX.X #13) ---

export const SheetSizeConfigSchema = z.object({
  mode: z.enum(['uniform', 'mixed']),
  uniformSheet: z.object({ length: z.number(), width: z.number() }).optional(),
  mixedVariants: z
    .tuple([
      z.object({ length: z.number(), width: z.number() }),
      z.object({ length: z.number(), width: z.number() }),
    ])
    .optional(),
});

// --- Настройки движка (TRD §4.1; дефолты вербатим оттуда) ---

export const CuttingSettingsSchema = z.object({
  kerf: z.number().min(0).max(20).default(4),
  edgeOffset: z.number().min(0).max(50).default(10),
  edgeOverhang: z.number().min(0).max(100).default(50), // v3.12 — §5.1.11
  method: z.enum(['length', 'width', 'optimal']).default('optimal'),
  startPoint: z.enum(['tl', 'tr', 'bl', 'br']).default('tl'),
  preferLongOffcuts: z.boolean().default(true), // §5.1.8
  preferWideOffcuts: z.boolean().default(true), // §5.1.8
  sheetConfig: SheetSizeConfigSchema, // v3.11 — §5.1.9
  // Расширенные опциональные поля production-конфигурации
  offcutEdgeOffset: z.number().nonnegative().max(100).optional(),
  edgeOffsetTop: z.number().nonnegative().optional(),
  edgeOffsetBottom: z.number().nonnegative().optional(),
  edgeOffsetLeft: z.number().nonnegative().optional(),
  edgeOffsetRight: z.number().nonnegative().optional(),
  minStripWidth: z.number().nonnegative().optional(),
  maxStripLength: z.number().positive().optional(),
  maxCarriageRun: z.number().positive().optional(),
  uporLimit: z.number().positive().optional(), // ограничение упоров (для Altendorf)
  considerTexture: z.boolean().optional(),
  useOffcutsFirst: z.boolean().optional(),
  machineType: z.enum(['guillotine', 'nesting']).optional(),
  machinePreset: z.string().optional(),
  minOffcutSize: z.number().nonnegative().optional(),
  rounding: z.number().positive().optional(),
  packetMode: z.boolean().optional(),
  materialType: z.enum(['sheet', 'linear']).default('sheet').optional(),
  // Минимальный размер детали, мм (ADR-0087) — зеркало контракта. Деталь
  // недопустима, если короткая сторона < minPartSize. `.default(40)`, 0 = выкл.
  minPartSize: z.number().min(0).max(1000).default(40),
});

// --- Метаданные проекта (TRD §4.1 + вкладка «Настройки» PR #5-PARAMS) ---
//
// `materialText` — обязательное free-text-поле материала проекта (P-001, §IX.X #1);
// в редакторе хранится здесь, при сохранении проекта уходит в серверный
// `CuttingProjectSchema.materialText` (там min(1)). В локальной модели default ''
// (документ создаётся пустым, заполняется на вкладке Настройки).
//
// `materialThickness` / `createdDate` — UI-only поля прототипа (project.html:158,171),
// которых НЕТ в замороженном контракте ADR-0065 (P-011). Хранятся в local state для
// контролируемого ввода и переживания смены вкладок, но НЕ персистятся в контракт/
// region-db в этом PR (вариант B супервайзера). Довести в контракт+ADR — EP-3
// (BACKLOG B-211). R2-симметрия честная: эти поля в roundtrip не участвуют.

export const ProjectMetaSchema = z.object({
  name: z.string().max(200),
  materialText: z.string().max(500).default(''),
  customerName: z.string().max(200).optional(),
  dueDate: z.string().date().optional(),
  notes: z.string().max(2000).optional(),
  materialThickness: z.number().nonnegative().optional(), // UI-only (B-211)
  createdDate: z.string().optional(), // UI-only (B-211) — «Дата» прототипа
});

// --- Кромочные ленты UI (вкладка «Настройки» → Кромки, project.html:133-149) ---
//
// Прототип: 2 кромки, у каждой название (text) + толщина (number). Контракт
// ADR-0065 `EdgesConfig` хранит ОДНУ строку-метку на кромку (`type1`/`type2`),
// толщины там нет. Поэтому: `name` персистится в `data.edges.type1/type2`
// (см. projectSync), `thickness` — UI-only (как materialThickness, B-211).

export const EdgeBandSchema = z.object({
  name: z.string().max(200).default(''),
  thickness: z.number().nonnegative().optional(), // UI-only (B-211)
});

export const CuttingEdgesSchema = z.object({
  edge1: EdgeBandSchema.default({ name: '' }),
  edge2: EdgeBandSchema.default({ name: '' }),
});

// --- Запись склада остатков (TRD §4.1, cutting_warehouse) ---
// OffcutSchema = WarehouseItemSchema (алиас прототипа `pomoshnik_offcuts_v1`, §0.4).

export const WarehouseItemSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  length: z.number().positive(),
  width: z.number().positive(),
  qty: z.number().int().positive(),
  material: z.string(),
  sourceProjectId: z.string().uuid().optional(),
  createdAt: z.string().datetime(),
});

export const OffcutSchema = WarehouseItemSchema;

// --- Складская запись остатка (feature-local local-first, PR #13, CUT-3.4/3.5) ---
//
// ⚠️ НЕ путать с замороженным `WarehouseItemSchema`/`OffcutSchema` (серверный склад,
// требует `userId`/`createdAt`/uuid и НЕ имеет «Имя/Партия»). Это **local-first**
// форма инвентаря остатков (cross-project, localStorage `pomoshnik_offcuts_v1`),
// зеркало `ProjectOffcut` + лёгкие метаданные — чтобы склад сохранял «Имя/Партия»
// детали-остатка и map `ProjectOffcut ↔ склад` был тривиальным (roundtrip честный).
//
// Решение владельца (STOP-pre PR #13, 12.06.2026): local-first форма с `name`,
// БЕЗ обязательного `userId` (склад живёт на устройстве). Серверный контракт
// `WarehouseItem` (с `userId`/uuid/`createdAt`) — будущий B-234 + ADR (как B-225
// для остатков проекта). `createdAt`/`sourceProjectId` — опциональные метаданные
// (происхождение записи), не обязательны для local-first.
export const WarehouseOffcutSchema = z.object({
  id: z.string(),
  length: z.number().nonnegative().max(10000),
  width: z.number().nonnegative().max(10000),
  qty: z.number().int().positive().max(1000),
  name: z.string().max(200), // «Имя/Партия» (унаследовано из ProjectOffcut)
  material: z.string().max(500), // free-text (P-001)
  createdAt: z.string().optional(), // ISO-время добавления в склад (метаданные)
  sourceProjectId: z.string().optional(), // происхождение (метаданные)
});

// --- Редактируемый документ раскроя (feature-local PR #1) ---
// Минимальная единица локального стора: метаданные + настройки (включают
// sheetConfig) + список деталей + выбор «участвующих» складских остатков (PR #24).
//
// PR #24 (ADR-0081): прежнее `offcuts: ProjectOffcut[]` упразднено — таблица
// Остатков стала складом (cross-project, localStorage `pomoshnik_offcuts_v1`,
// reactive-зеркало в сторе). Per-project хранится лишь ВЫБОР «участвуют в раскрое»:
// `participatingOffcutIds` — id записей склада. Зеркало в `ProjectData`
// (durable через сервер, ADR-0081). Старые персисты с `offcuts` парсятся
// back-compat (неизвестный ключ отбрасывается, выбор дефолтит в []).
export const CuttingDocumentSchema = z.object({
  meta: ProjectMetaSchema,
  settings: CuttingSettingsSchema,
  parts: z.array(PartSchema),
  edges: CuttingEdgesSchema.default({ edge1: { name: '' }, edge2: { name: '' } }),
  participatingOffcutIds: z.array(z.string()).default([]), // PR #24 (ADR-0081)
});
